import { Check, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils.js';

interface ImportStepperProps {
  /** Translated step labels in order. */
  steps: string[];
  /** Zero-based index of the currently active step, or `null` when the
   * flow hasn't started yet — every step then renders as upcoming. */
  currentStep: number | null;
  /** Accessible label for the nav landmark (pass a translated string). */
  ariaLabel?: string;
  /** Visually-hidden per-state status text, announced next to each step's
   * label so a screen reader gets more than a bare label (translated by the
   * caller — this component stays i18n-agnostic). */
  stepStatusLabels?: {
    done: string;
    current: string;
    upcoming: string;
  };
}

// The current step is a state indicator, not a control. A solid `bg-primary` fill
// is what every primary button in this app wears, so it read as clickable; the
// tint keeps the brand colour without borrowing the button's weight.
function getStepLabelClassName(active: boolean, done: boolean): string {
  return cn(
    'flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors',
    // text-primary on bg-primary/10 only reaches ~3.7:1 (max ~4.2:1 even with no tint at
    // all), below WCAG AA's 4.5:1 for 13px text. --primary-emphasis (globals.css) is the
    // same hue at a lightness that clears AA against this tint, so both the fill and the
    // text stay token-derived and keep tracking --primary on a rebrand.
    active && 'bg-primary/10 text-primary-emphasis font-semibold',
    done && !active && 'text-emerald-700',
    !active && !done && 'text-muted-foreground',
  );
}

function getStepIndexClassName(active: boolean): string {
  return cn(
    'flex h-5 w-5 items-center justify-center rounded-full text-2xs font-bold shrink-0',
    active ? 'bg-primary text-primary-foreground' : 'bg-muted',
  );
}

function resolveStepStatusLabel(
  done: boolean,
  active: boolean,
  stepStatusLabels: ImportStepperProps['stepStatusLabels'],
): string | undefined {
  if (!stepStatusLabels) return undefined;
  if (done) return stepStatusLabels.done;
  if (active) return stepStatusLabels.current;
  return stepStatusLabels.upcoming;
}

function StepItem({
  label,
  idx,
  done,
  active,
  isLast,
  statusLabel,
}: {
  label: string;
  idx: number;
  done: boolean;
  active: boolean;
  isLast: boolean;
  statusLabel: string | undefined;
}) {
  return (
    <div className="flex items-center">
      <div
        className={getStepLabelClassName(active, done)}
        data-testid={`step-${idx}`}
        aria-current={active ? 'step' : undefined}
      >
        {done ? (
          <Check className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <span className={getStepIndexClassName(active)}>{idx + 1}</span>
        )}
        {label}
        {statusLabel ? <span className="sr-only">{` (${statusLabel})`}</span> : null}
      </div>
      {!isLast && <ChevronRight className="h-4 w-4 text-muted-foreground/40 mx-1 shrink-0" />}
    </div>
  );
}

/**
 * Horizontal wizard stepper for multi-step import flows.
 * Completed steps show a checkmark; the active step is highlighted;
 * future steps are muted.
 */
export function ImportStepper({
  steps,
  currentStep,
  ariaLabel,
  stepStatusLabels,
}: ImportStepperProps) {
  return (
    <nav aria-label={ariaLabel} className="flex items-center gap-0" data-testid="import-stepper">
      {steps.map((label, idx) => {
        const done = currentStep != null && idx < currentStep;
        const active = currentStep != null && idx === currentStep;
        return (
          <StepItem
            key={label}
            label={label}
            idx={idx}
            done={done}
            active={active}
            isLast={idx === steps.length - 1}
            statusLabel={resolveStepStatusLabel(done, active, stepStatusLabels)}
          />
        );
      })}
    </nav>
  );
}
